
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ArchiveRestore, FileArchive, RefreshCw } from 'lucide-react';
import { listAdminBackups, restoreFromLocalAdminBackup } from '@/services/backup/adminBackup';
import { motion } from 'framer-motion';

interface AdminBackupEntry {
  id: string;
  timestamp: string;
  description: string;
  size: number;
}

interface AdminBackupListProps {
  onRestoreComplete?: () => void;
}

const AdminBackupList: React.FC<AdminBackupListProps> = ({ onRestoreComplete }) => {
  const [backups, setBackups] = useState<AdminBackupEntry[]>([]);
  const [isRestoring, setIsRestoring] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    loadBackups();
  }, []);
  
  const loadBackups = () => {
    setIsLoading(true);
    try {
      const adminBackups = listAdminBackups();
      // Sort backups by timestamp (newest first)
      const sortedBackups = [...adminBackups].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setBackups(sortedBackups);
    } catch (error) {
      console.error('Error loading admin backups:', error);
      toast.error('Failed to load admin backups');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRestore = async (backupId: string) => {
    if (isRestoring) return;
    
    setIsRestoring(backupId);
    try {
      const success = await restoreFromLocalAdminBackup(backupId);
      if (success) {
        toast.success('Admin data restored successfully');
        if (onRestoreComplete) {
          onRestoreComplete();
        }
      }
    } catch (error) {
      console.error('Failed to restore admin backup:', error);
      toast.error('Failed to restore admin backup');
    } finally {
      setIsRestoring(null);
    }
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (backups.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileArchive className="h-16 w-16 mx-auto mb-2 text-gray-300" />
        <p>No admin backups available</p>
        <p className="text-sm">Create a backup to see it here</p>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-auto max-h-60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Size</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {backups.map((backup) => (
                <motion.tr
                  key={backup.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="border-b"
                >
                  <TableCell className="font-medium">
                    {formatDistanceToNow(new Date(backup.timestamp), { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-gray-600">
                    {backup.description}
                  </TableCell>
                  <TableCell>
                    {formatFileSize(backup.size)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(backup.id)}
                      disabled={isRestoring !== null}
                      className="h-8"
                    >
                      {isRestoring === backup.id ? (
                        <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <ArchiveRestore className="h-3.5 w-3.5 mr-1" />
                      )}
                      Restore
                    </Button>
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminBackupList;
